import React from 'react'
import {Text, useMultiStyleConfig, StackProps, Link, TextProps, FlexProps, Flex} from '@chakra-ui/react'
import {createRedirectUrl} from '_utils'
import {usePageDesigner} from '_customHooks'

interface CtaButtonProps {
    href: string
    variant?: 'button' | 'text' | 'outline' | 'textBorderTop'
    background?: string
    ctaColor?: string
    hoverBackground?: string
    hoverCtaColor?: string
    borderColor?: string
    title?: string
    isAnimated?: boolean
    animationTime?: string
    onClick?: (e) => void
    children: React.ReactNode
    openInNewTab?: boolean
    size?: 'xs' | 'sm' | 'md' | 'lg'
}

const defaultSizes = {
    button: 'sm',
    outline: 'sm',
    text: 'md',
    textBorderTop: 'md',
} as {[ctaType: string]: 'xs' | 'sm' | 'md' | 'lg'}

/**
 * Renders a customizable Call-To-Action (CTA) button component.
 *
 * @param {string} href - The URL to navigate to when the button is clicked.
 * @param {'text' | 'button'} [variant='text'] - The visual variant of the CTA button.
 * @param {React.ReactNode} children - The content to display inside the button.
 * @param {boolean} [isAnimated=false] - Whether the CTA text underline should have animation effects.
 * @param {string} [ctaColor='color.text.white'] - The color of the CTA text.
 * @param {string} [background='color.text.black'] - The background color of the button.
 * @param {string} [hoverBackground='color.background.white'] - The background color on hover.
 * @param {string} [hoverCtaColor='color.text.black'] - The CTA text color on hover.
 * @param {string} [borderColor='color.text.black'] - The border color of the button.
 * @param {number} [animationTime] - The duration of the animation in milliseconds.
 * @param {string} [title] - The title attribute for the button, used for accessibility.
 * @param {(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void} [onClick] - Click event handler for the button.
 * @param {boolean} [openInNewTab] - If true, opens the link in a new browser tab.
 *
 * @returns {JSX.Element} The rendered CTA button component.
 */
const CtaButton = ({
    href,
    variant = 'text',
    children,
    isAnimated = false,
    ctaColor = 'color.text.white',
    background = 'color.text.black',
    hoverBackground = 'color.background.white',
    hoverCtaColor = 'color.text.black',
    borderColor = 'color.text.black',
    animationTime,
    title,
    onClick,
    openInNewTab,
    size = defaultSizes[variant] || 'md',
    ...props
}: CtaButtonProps): JSX.Element => {
    const styles = useMultiStyleConfig('CtaButton', {
        variant,
        isAnimated,
        animationTime,
        background,
        ctaColor,
        hoverBackground,
        hoverCtaColor,
        borderColor,
        size,
    }) as Record<string, StackProps | FlexProps | TextProps>
    const {linkObject} = usePageDesigner()
    return (
        <Flex
            as={Link}
            sx={styles.linkWrapper as FlexProps}
            href={createRedirectUrl(href, linkObject.hostlink, linkObject.locale, linkObject.site)}
            title={title}
            data-testid={`cta-button-${variant === 'text' ? 'text' : 'button'}`}
            onClick={onClick}
            className="cc-content-button cc-content-button-text"
            target={openInNewTab ? '_blank' : '_self'}
            {...props}
        >
            <Text {...(styles.linkText as TextProps)}>{children}</Text>
        </Flex>
    )
}

export {CtaButton}
